import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  EARNINGS_RANGE_DAYS,
  EARNINGS_RANGE_LABELS,
  EARNINGS_RANGES,
  EARNINGS_SERIES_MAX_DAYS,
  CRYPTO_NETWORK_LABELS,
  PAYOUT_METHOD_LABELS,
  shortHash,
  EMAIL_NOT_VERIFIED_MESSAGE,
  addDays,
  daysInclusive,
  formatCents,
  utcDay,
  type EarningsRange,
  type EarningsSeriesResponse,
  type PayoutMethodKind,
  type WalletResponse,
} from '@nexus/shared';

import { useSession } from '../auth/SessionContext';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { usePublicConfig } from '../lib/publicConfig';
import {
  Alert,
  AreaChart,
  Badge,
  Button,
  Card,
  checkName,
  DataTable,
  EmptyState,
  ExternalLink,
  Fact,
  Facts,
  LinkButton,
  LoadErrorPage,
  LoadingPage,
  Money,
  PageHeader,
  Pill,
  Pills,
  Stat,
  StatGrid,
  TextField,
  When,
  formatMonth,
  formatWhen,
  statusTone,
  type ChartPoint,
  type Column,
} from '../ui';

/**
 * The wallet.
 *
 * ## Four numbers, and where each one comes from
 *
 * Available is the earnings that are past the holdback, not held by a fraud
 * check and not already in a payout. Pending is the rest of what is owed, with
 * the reason on every row — the holdback date, or the sentence the check that
 * held it wrote. In a payout is money that has been asked for and is waiting
 * for Monday. Paid to date is what has actually left.
 *
 * Every one of them is a sum of ledger entries on the server; none is stored.
 * The entries are on this page, under "The ledger behind these figures",
 * because a creator who is told a number about their own money should be able
 * to see the rows it came from without asking anybody.
 *
 * ## Why a held row says why
 *
 * The alternative — a total that is smaller than the creator expected, with no
 * explanation — is the single fastest way to lose a creator, and the reason is
 * already written down: `Earning.heldReason` is the sentence the rule wrote,
 * with its own numbers in it. The rule's NAME is written out too, because
 * `CURVE_SHAPE` is a column value and not something anybody has to read.
 */
export function Wallet(): React.JSX.Element {
  const { session } = useSession();
  /* Same gate as POST /wallet/payout-request: an unconfirmed address cannot
     ask to be paid, and the button says so instead of failing. */
  const emailVerified = session?.user.emailVerified !== false;
  const [data, setData] = useState<WalletResponse | null>(null);
  const config = usePublicConfig();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showEntries, setShowEntries] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await apiGet<WalletResponse>('/wallet'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your wallet.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function requestPayout(): Promise<void> {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const body = await apiSend<{ payout: { amountCents: number } }>(
        'POST',
        '/wallet/payout-request',
        {},
      );
      setMessage(
        `Requested ${formatCents(body.payout.amountCents)}. It joins the next Monday batch, and you will see it here once it has been approved and paid.`,
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not request a payout.');
    } finally {
      setBusy(false);
    }
  }

  if (error && data === null) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="Wallet"
        heading="Could not load your wallet."
        onRetry={() => void load()}
      >
        <span>{error} Nothing you are owed is affected.</span>
      </LoadErrorPage>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Creator" title="Wallet" stats />;

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="Wallet"
        lead="Every figure here is the sum of ledger entries, not a stored balance — which is why they always add up."
      />

      <EarningsOverTime />

      <Card variant="feature">
        <StatGrid>
          <Stat
            label="Available"
            value={<Money cents={data.availableCents} />}
            note="Past the holdback and cleared."
            emphasis
          />
          <Stat
            label="Pending"
            value={<Money cents={data.pendingCents} />}
            note="Owed, but not payable yet."
          />
          <Stat
            label="In a payout"
            value={<Money cents={data.requestedCents + data.inTransitCents} />}
            note="Asked for, waiting for Monday."
          />
          <Stat label="Paid to date" value={<Money cents={data.lifetimePaidCents} />} />
        </StatGrid>

        {/* A SUBSET of the four figures above, never an addition to them: a
            referral bonus is an ordinary earning credited to this creator, so
            it is already inside Available, Pending and Paid. Said explicitly,
            because a creator who adds this line to the totals and finds it does
            not match has caught the site lying to them. */}
        {data.referral.lifetimeCents > 0 && (
          <p className="muted">
            <Money cents={data.referral.lifetimeCents} /> of this is from{' '}
            <Link to="/referrals">referrals</Link> — <Money cents={data.referral.availableCents} />{' '}
            available, <Money cents={data.referral.pendingCents} /> pending and{' '}
            <Money cents={data.referral.paidCents} /> already paid. It is counted in the figures
            above, not on top of them.
          </p>
        )}

        <div className="actions">
          <Button
            variant="primary"
            onClick={() => void requestPayout()}
            disabled={!data.canRequest || busy || !emailVerified}
          >
            {/* A button that says "Request $0.00" is a button asking for
                nothing, and it is the first thing a creator sees on day one.
                With something in the wallet it still names the figure, even
                when that figure is under the minimum: what is there is the
                thing they want to know. */}
            {busy
              ? 'Requesting…'
              : data.availableCents > 0
                ? `Request ${formatCents(data.availableCents)}`
                : 'Request a payout'}
          </Button>
          {!emailVerified ? (
            <span className="muted">{EMAIL_NOT_VERIFIED_MESSAGE}</span>
          ) : (
            !data.canRequest &&
            data.blockedReason && <span className="muted">{data.blockedReason}</span>
          )}
        </div>

        {message && <Alert tone="positive">{message}</Alert>}
        {error && <Alert tone="critical">{error}</Alert>}

        <p className="muted">
          The minimum payout is {formatCents(data.minimumCents)}. Requests are paid in a batch
          every Monday, after the operator approves it. A payout is everything available at the
          moment you ask — there is nothing to choose.
        </p>
      </Card>

      <div className="split">
        <Card
          title="Available now"
          sub="Earnings that are past the holdback and clear of every check."
        >
          <AvailableTable data={data} />
        </Card>

        <Card title="Pending" sub="Owed to you, with the reason it is not payable yet.">
          {data.pending.length === 0 ? (
            <EmptyState title="Nothing is on hold or inside the holdback.">
              Everything you have earned is either available or already paid.
            </EmptyState>
          ) : (
            <ul className="rows">
              {data.pending.map((row) => (
                <li key={row.earningId}>
                  <div className="row-head">
                    <span className="row-title">
                      <Money cents={row.amountCents} />
                    </span>
                    {row.heldCheck ? (
                      <Badge tone="critical">{checkName(row.heldCheck)}</Badge>
                    ) : (
                      <Badge tone="waiting">In the holdback</Badge>
                    )}
                  </div>
                  <span className="muted">
                    {row.referralRefereeName
                      ? `Referral bonus from ${row.referralRefereeName}`
                      : row.campaignName}
                  </span>
                  {/* The reason, verbatim, because a number that is smaller than
                      expected with no explanation is how a creator stops posting. */}
                  <span>{row.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Payouts" sub="Every request, and every attempt at paying it.">
        <PayoutHistory data={data} />
      </Card>

      <Card title="Payout details">
        {data.payoutProfile.hasMethod ? (
          <Facts>
            <Fact label="Method">
              {data.payoutProfile.methodKind
                ? PAYOUT_METHOD_LABELS[data.payoutProfile.methodKind as PayoutMethodKind]
                : '—'}
            </Fact>
            {data.payoutProfile.cryptoNetwork && (
              <Fact label="Network">
                <span data-testid="wallet-network">
                  {CRYPTO_NETWORK_LABELS[data.payoutProfile.cryptoNetwork]}
                </span>
              </Fact>
            )}
            {/* The MASK. The full value is never returned to this app — for
                USDT that is the last six characters of the address. */}
            <Fact label="Sent to">{data.payoutProfile.methodLabel}</Fact>
            {/* Where the money comes FROM, so a PayPal payment from an address
                the creator does not recognise is not mistaken for somebody
                else's. Only when the operator has said, and only for PayPal. */}
            {data.payoutProfile.methodKind === 'PAYPAL' && config?.paidFrom.paypalEmail && (
              <Fact label="Paid from">
                <span data-testid="paid-from">{config.paidFrom.paypalEmail}</span>
              </Fact>
            )}
            <Fact label="Automatic">
              {data.payoutProfile.autoRequest
                ? 'On — you join every Monday batch above the minimum'
                : 'Off — you ask each time'}
            </Fact>
          </Facts>
        ) : (
          <EmptyState
            title="Nexus has nowhere to send your money yet."
            action={
              <Link className="btn btn-primary" to="/profile">
                Add your payout details
              </Link>
            }
          >
            Earnings keep accruing without it, but a payout cannot be sent until there is a
            destination on file.
          </EmptyState>
        )}
      </Card>

      <Card
        title="The ledger behind these figures"
        sub="Every number above is the sum of these entries. Nothing is a stored balance, which is why they always add up."
      >
        {/* No disclosure for nothing: "Show 0 entries" is a control that
            does nothing, offered to somebody on their first day. */}
        {data.entries.length === 0 ? (
          <p className="muted">
            Nothing has moved yet. Your first entry is written the moment a clip of yours is
            approved.
          </p>
        ) : (
          <>
            <div className="actions">
              <LinkButton onClick={() => setShowEntries((s) => !s)}>
                {showEntries ? 'Hide the entries' : `Show ${data.entries.length} entries`}
              </LinkButton>
            </div>
            {showEntries && <EntriesTable data={data} />}
          </>
        )}
      </Card>
    </>
  );
}

function AvailableTable({ data }: { data: WalletResponse }): React.JSX.Element {
  type Row = WalletResponse['available'][number];
  const columns: Array<Column<Row>> = [
    {
      key: 'campaign',
      header: 'Campaign',
      primary: true,
      /* A referral bonus carries the campaign of the earning it came from —
         `Earning.campaignId` is NOT NULL — and showing that here would put a
         campaign this creator may never have posted to beside their money.
         `referralRefereeName` is what tells the two rows apart. */
      cell: (row) =>
        row.referralRefereeName ? (
          <>
            Referral bonus
            <span className="cell-note">from {row.referralRefereeName}</span>
          </>
        ) : (
          row.campaignName
        ),
    },
    {
      key: 'post',
      header: 'Post',
      cell: (row) =>
        row.postUrl ? (
          <ExternalLink href={row.postUrl}>the clip</ExternalLink>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'since',
      header: 'Payable since',
      cell: (row) => <When value={row.payableAfter} />,
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      align: 'end',
      cell: (row) => <Money cents={row.amountCents} />,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data.available}
      getKey={(row) => row.earningId}
      empty={
        <EmptyState title="Nothing is ready to be paid yet.">
          An earning becomes available {'≈'} 3 days after its 72-hour reading, once the
          holdback is over.
        </EmptyState>
      }
    />
  );
}

function PayoutHistory({ data }: { data: WalletResponse }): React.JSX.Element {
  type Payout = WalletResponse['payouts'][number];
  const columns: Array<Column<Payout>> = [
    {
      key: 'status',
      header: 'Status',
      primary: true,
      cell: (payout) => (
        <>
          <Badge
            tone={statusTone(payout.requested ? 'REQUESTED' : payout.status, 'payout')}
          >
            {payout.requested ? 'Requested' : payoutWord(payout.status)}
          </Badge>
          {payout.failureReason && <span className="cell-note">{payout.failureReason}</span>}
          {/* Every attempt, not just the last: "we tried on the 3rd and it
              bounced" is the answer to the only question a creator asks about a
              late payment. */}
          {payout.attempts.map((attempt) => (
            <span key={attempt.id} className="cell-note">
              attempt {attempt.attemptNumber}: {attempt.status.toLowerCase()} on{' '}
              <When value={attempt.submittedAt} />
            </span>
          ))}
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      align: 'end',
      cell: (payout) => <Money cents={payout.amountCents} />,
    },
    { key: 'requested', header: 'Requested', cell: (p) => <When value={p.requestedAt} /> },
    { key: 'paid', header: 'Paid', cell: (p) => <When value={p.paidAt} /> },
    {
      key: 'reference',
      header: 'Reference',
      cell: (p) =>
        /* A USDT payout's reference is the transaction hash, and the chain it
           went out on is what a creator checks first: the explorer link is
           the proof the money left. */
        p.settledVia?.cryptoNetwork && p.paymentReference ? (
          <span data-testid="paid-in-usdt">
            Paid in USDT on {p.settledVia.networkLabel}, tx{' '}
            {p.settledVia.explorerUrl ? (
              <a href={p.settledVia.explorerUrl} target="_blank" rel="noreferrer noopener">
                <code className="chip quiet">{shortHash(p.paymentReference)}</code>
              </a>
            ) : (
              <code className="chip quiet">{shortHash(p.paymentReference)}</code>
            )}
          </span>
        ) : p.paymentReference ? (
          <code className="chip quiet">{p.paymentReference}</code>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'help',
      header: 'Help',
      cell: (p) => (
        <Link to={`/support/new?category=PAYOUT&refType=Payout&refId=${encodeURIComponent(p.id)}`}>
          Something wrong with a payout?
        </Link>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data.payouts}
      getKey={(payout) => payout.id}
      empty={
        <EmptyState title="You have not been paid yet.">
          Your first payout appears here the Monday after you are above the minimum.
        </EmptyState>
      }
    />
  );
}

function EntriesTable({ data }: { data: WalletResponse }): React.JSX.Element {
  type Entry = WalletResponse['entries'][number];
  const columns: Array<Column<Entry>> = [
    { key: 'what', header: 'What', primary: true, cell: (e) => e.description },
    { key: 'posted', header: 'Posted', cell: (e) => <When value={e.postedAt} /> },
    { key: 'account', header: 'Account', cell: (e) => e.accountName },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      align: 'end',
      cell: (e) => <Money cents={e.amountCentsSigned} />,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data.entries}
      getKey={(entry) => entry.entryId}
      empty={<EmptyState title="No entries yet.">Your first earning writes the first one.</EmptyState>}
    />
  );
}

/** The operator's payout words, in the creator's terms. */
function payoutWord(status: string): string {
  switch (status) {
    case 'DRAFT':
      return 'Drafted';
    case 'APPROVED':
      return 'Approved, being sent';
    case 'PROCESSING':
      return 'Being sent';
    case 'PAID':
      return 'Paid';
    case 'FAILED':
      return 'Failed';
    case 'REVERSED':
      return 'Reversed';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
}

/* ------------------------------------------------------------------ */
/* Earnings over time                                                  */
/* ------------------------------------------------------------------ */

const RANGE_VALUES: readonly string[] = EARNINGS_RANGES;

/** What the URL says, defended against anything a person can type into it. */
function readRange(params: URLSearchParams): {
  range: EarningsRange;
  from: string;
  to: string;
} {
  const raw = params.get('range') ?? '';
  const range = (RANGE_VALUES.includes(raw) ? raw : '30d') as EarningsRange;
  return { range, from: params.get('from') ?? '', to: params.get('to') ?? '' };
}

/** `YYYY-MM-DD` only. Anything else is treated as not given. */
const isDay = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * The chart above the figures.
 *
 * ## Why the selection is in the URL
 *
 * Same reason "My submissions" puts its sort there: a reload keeps it, the back
 * button works, and a creator can send the operator the exact view they are
 * looking at — which for a question about money is the whole conversation. A
 * range held in `useState` is a range that vanishes on every refresh, and a
 * refresh is exactly what somebody does while waiting on a payout.
 *
 * ## Why a custom range is checked here as well as at the API
 *
 * The series is zero-filled, so the day count IS the response size and the API
 * refuses more than `EARNINGS_SERIES_MAX_DAYS`. Checking it here too means a
 * creator who types 2019 in the From box gets a sentence about the limit instead
 * of a 400 — and both numbers come from the one constant, so they cannot drift.
 */
function EarningsOverTime(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const { range, from, to } = readRange(params);

  const [series, setSeries] = useState<EarningsSeriesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* The query the API is asked for, and the reason it is refused when it is. */
  const request = useMemo<{ search: string; refusal: string | null }>(() => {
    if (range === 'all') return { search: '', refusal: null };
    if (range === 'custom') {
      /* A half-filled custom range is not an error, it is somebody mid-typing:
         the chart keeps showing what it has until both boxes are days. */
      if (!isDay(from) || !isDay(to)) {
        return { search: '', refusal: 'Pick both dates to see a custom range.' };
      }
      if (to < from) return { search: '', refusal: 'The end of the range is before its start.' };
      const days = daysInclusive(from, to);
      if (days > EARNINGS_SERIES_MAX_DAYS) {
        return {
          search: '',
          refusal: `A custom range covers at most ${EARNINGS_SERIES_MAX_DAYS} days, and that one is ${days.toLocaleString('en-US')}. Pick a shorter one, or use All time.`,
        };
      }
      return { search: `?from=${from}&to=${to}`, refusal: null };
    }
    const today = utcDay(new Date());
    const start = addDays(today, -(EARNINGS_RANGE_DAYS[range] - 1));
    return { search: `?from=${start}&to=${today}`, refusal: null };
  }, [range, from, to]);

  useEffect(() => {
    if (request.refusal !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiGet<EarningsSeriesResponse>(
          `/wallet/earnings-series${request.search}`,
        );
        /* A shape check, cheap, because a chart that throws takes the whole
           Wallet page down with it — and the four figures a creator came for are
           a DIFFERENT request that succeeded. `apiGet` does not validate, so this
           is the only place the body is looked at. */
        if (!Array.isArray(body?.points) || typeof body?.totals?.earnedCents !== 'number') {
          throw new Error('malformed series');
        }
        if (!cancelled) {
          setSeries(body);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Could not load your earnings history.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request]);

  function setRange(value: EarningsRange): void {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('range', value);
      if (value !== 'custom') {
        next.delete('from');
        next.delete('to');
      }
      return next;
    });
  }

  function setDay(key: 'from' | 'to', value: string): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set('range', 'custom');
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      /* `replace`, because dragging a date picker is not navigation and would
         otherwise leave a history entry per day tried. */
      { replace: true },
    );
  }

  const points: ChartPoint[] = (series?.points ?? []).map((point) => ({
    key: point.date,
    /* A month bucket is named by its month, not by its first day: "Sep 2026" and
       not "1 Sep 2026", which would read as one day's earnings. */
    label:
      series?.bucket === 'month'
        ? formatMonth(`${point.date}T00:00:00.000Z`)
        : formatWhen(`${point.date}T00:00:00.000Z`),
    value: point.earnedCents,
    compare: point.paidCents,
  }));

  /* "1 Sep 2026 to 24 Sep 2026" and not "2026-09-01 to 2026-09-24": every other
     date on this page is written the way a person says it, and this string ends
     up inside a sentence ("... paid out over ...") and inside the chart's own
     aria-label, which is read aloud. */
  const customDays = range === 'custom' && isDay(from) && isDay(to);
  const rangeName = customDays
    ? `${formatWhen(`${from}T00:00:00.000Z`)} to ${formatWhen(`${to}T00:00:00.000Z`)}`
    : EARNINGS_RANGE_LABELS[range];
  /* The same words inside a sentence. A preset reads better lower-cased ("over
     30 days"); a pair of dates must not be ("over 1 sep 2026"). */
  const rangeSentence = customDays ? rangeName : rangeName.toLowerCase();

  return (
    <Card
      title="Earnings over time"
      sub="Earned is bucketed on the day the money became payable. Paid out is the day it left."
      tight
    >
      <div className="range-controls">
        <Pills label="Range">
          {EARNINGS_RANGES.map((value) => (
            <Pill key={value} selected={range === value} onClick={() => setRange(value)}>
              {EARNINGS_RANGE_LABELS[value]}
            </Pill>
          ))}
        </Pills>

        {range === 'custom' && (
          <div className="range-dates">
            <TextField
              label="From"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(event) => setDay('from', event.target.value)}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(event) => setDay('to', event.target.value)}
            />
          </div>
        )}
      </div>

      {request.refusal !== null ? (
        <Alert tone="waiting">{request.refusal}</Alert>
      ) : error ? (
        <Alert tone="critical">{error}</Alert>
      ) : series === null ? (
        <div className="skeleton tall" aria-hidden="true" />
      ) : (
        <>
          <AreaChart
            points={points}
            valueName="earned"
            compareName="paid out"
            formatValue={(cents) => formatCents(cents)}
            summary={`Earnings over ${rangeName}: ${formatCents(series.totals.earnedCents)} earned and ${formatCents(series.totals.paidCents)} paid out across ${points.length} ${series.bucket === 'month' ? 'months' : 'days'}, from ${series.from} to ${series.to}.`}
            /* A NOTE, not a replacement. The flat zero line across the dated
               axis is the picture a creator on day one should see; what it
               needs beside it is when the first bar will appear. */
            emptyNote={
              series.allTime
                ? 'Nothing yet — your first earning is recorded 72 hours after your first approved clip goes live, and it will appear here on the day it becomes payable.'
                : 'Nothing in this range. Try a longer one, or All time.'
            }
          />
          <p className="muted">
            {formatCents(series.totals.earnedCents)} earned and{' '}
            {formatCents(series.totals.paidCents)} paid out over {rangeSentence}
            {series.bucket === 'month' &&
              ' — grouped by month, because the range is longer than a year'}
            .
          </p>

          <EarningsRows series={series} points={points} />
        </>
      )}
    </Card>
  );
}


/* ------------------------------------------------------------------ */
/* The same series, as dates and figures                               */
/* ------------------------------------------------------------------ */

/** How many rows are shown before the disclosure. */
const ROWS_BEFORE_SHOW_ALL = 10;

/**
 * The chart's own numbers, written out.
 *
 * ## Why a table under a chart is not a duplicate
 *
 * A chart answers "which days were good" and cannot answer "what exactly did I
 * earn on the 14th" — the read-out does that one bucket at a time, with a mouse
 * or an arrow key, which is no way to check a month. A creator reconciling a
 * payout is reading down a column, and the operator asked for exactly that: a
 * dated table.
 *
 * NEWEST FIRST, because the question is almost always about the last few days,
 * and collapsed to ten rows with the rest behind a disclosure: ninety rows
 * under the chart would push every other figure on the Wallet off the screen.
 *
 * ## The CSV is the same array
 *
 * It is built from `series.points`, the rows in the table, and not from a
 * second request — a download that disagreed with the screen it was downloaded
 * from is the worst possible version of this feature. A `data:` URL rather than
 * `URL.createObjectURL`, because there is no object to revoke and no lifecycle
 * to get wrong; 400 buckets of two integers is a few kilobytes.
 */
function EarningsRows({
  series,
  points,
}: {
  series: EarningsSeriesResponse;
  points: ChartPoint[];
}): React.JSX.Element {
  const [showAll, setShowAll] = useState(false);

  /* Newest first. `points` is oldest-first because that is the order a line is
     drawn in; a table is read the other way. */
  const rows = useMemo(() => [...points].reverse(), [points]);
  const shown = showAll ? rows : rows.slice(0, ROWS_BEFORE_SHOW_ALL);
  const bucketWord = series.bucket === 'month' ? 'month' : 'day';

  const csv = useMemo(() => {
    const lines = [
      `${bucketWord},earned,paid_out`,
      ...rows.map(
        (row) =>
          `${row.key},${(row.value / 100).toFixed(2)},${(row.compare / 100).toFixed(2)}`,
      ),
    ];
    return `data:text/csv;charset=utf-8,${encodeURIComponent(lines.join('\n'))}`;
  }, [rows, bucketWord]);

  const columns: Array<Column<ChartPoint>> = [
    { key: 'day', header: series.bucket === 'month' ? 'Month' : 'Day', primary: true, cell: (r) => r.label },
    {
      key: 'earned',
      header: 'Earned',
      numeric: true,
      align: 'end',
      cell: (r) => <Money cents={r.value} />,
    },
    {
      key: 'paid',
      header: 'Paid out',
      numeric: true,
      align: 'end',
      cell: (r) => <Money cents={r.compare} />,
    },
  ];

  return (
    <div className="stack tight" data-testid="earnings-table">
      <DataTable
        columns={columns}
        rows={shown}
        getKey={(row) => row.key}
        /* A DAY AND TWO FIGURES STAYS A TABLE ON A PHONE.
           Stacked into a card per day this was about 178px per row — a date, a
           label, a number, a label, a number — so ten days ran to 1,800px and
           "Show all 30" to more than 5,000. It is also the one table here
           somebody reads DOWN a column: which days paid. Three short cells fit
           390px with room over; the cards never fitted anything. */
        compact
        /* Unreachable in practice — the series is zero-filled, so a range
           always has at least one bucket — and written anyway, because
           `DataTable` requires an answer and "nothing at all" is not the same
           sentence as "nothing earned". */
        empty={<p className="muted">No {bucketWord}s in this range.</p>}
      />
      <div className="actions">
        {rows.length > ROWS_BEFORE_SHOW_ALL && (
          <LinkButton onClick={() => setShowAll((open) => !open)}>
            {showAll
              ? `Show the last ${ROWS_BEFORE_SHOW_ALL} ${bucketWord}s`
              : `Show all ${rows.length} ${bucketWord}s`}
          </LinkButton>
        )}
        <a className="btn btn-quiet small" href={csv} download={`nexus-earnings-${series.from}-to-${series.to}.csv`}>
          Download CSV
        </a>
      </div>
    </div>
  );
}
